import { useId, useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/utils'

export interface CollapsibleSectionProps {
  /** Section heading (always visible in the header button). */
  title: string
  /** Optional secondary line under the title. */
  subtitle?: string
  /** Optional node after the title, open or closed (a count badge); it joins the toggle's accessible name. */
  badge?: ReactNode
  /** One-line summary shown only when collapsed (e.g. "12 things collected, in order"). */
  summary?: ReactNode
  /** Optional leading icon/emoji node. */
  icon?: ReactNode
  /** Visual chrome: 'default' (neutral card) or 'soft' (Workshop-style
   * rounded-2xl card on basis tokens: neutral hairline + primary-tinted icon chip). */
  variant?: 'default' | 'soft'
  /** Uncontrolled initial open state (default true). Ignored when `open` is provided. */
  defaultOpen?: boolean
  /** Controlled open state. When provided, the component does not own state. */
  open?: boolean
  /** Fires with the requested next state whenever the user toggles. */
  onOpenChange?: (open: boolean) => void
  /** When set, uncontrolled open state persists to localStorage under this key. */
  storageKey?: string
  className?: string
  children: ReactNode
}

function readStored(storageKey: string | undefined, fallback: boolean): boolean {
  if (!storageKey || typeof window === 'undefined') return fallback
  const stored = window.localStorage.getItem(storageKey)
  if (stored === 'open') return true
  if (stored === 'closed') return false
  return fallback
}

/**
 * Collapsible section shell — generalizes PersonaWorkshop/WorkshopSection into
 * a shared primitive: chevron toggle, optional collapsed one-line summary,
 * aria-expanded/aria-controls wiring, and controlled-or-persisted open state.
 * Roots a <section> so callers can scope queries with `.closest('section')`.
 */
export function CollapsibleSection({
  title,
  subtitle,
  badge,
  summary,
  icon,
  variant = 'default',
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
  storageKey,
  className,
  children,
}: CollapsibleSectionProps) {
  const contentId = useId()
  const isControlled = controlledOpen !== undefined
  const [uncontrolledOpen, setUncontrolledOpen] = useState(() => readStored(storageKey, defaultOpen))
  const open = isControlled ? controlledOpen : uncontrolledOpen

  const toggle = () => {
    const next = !open
    if (!isControlled) {
      setUncontrolledOpen(next)
      if (storageKey && typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, next ? 'open' : 'closed')
      }
    }
    onOpenChange?.(next)
  }

  return (
    <section
      className={cn(
        // SA-1059 (UX pass spec move 1): the one section recipe — a white
        // surface on the page ground, the section-border token, shadow-xs.
        // `soft` keeps its radius, icon chip and primary-tinted shadow: a
        // named Workshop variant, not a hand-rolled copy.
        variant === 'soft'
          ? 'overflow-hidden rounded-2xl border border-section-border bg-card shadow-xs shadow-primary/5'
          : 'overflow-hidden rounded-lg border border-section-border bg-card shadow-xs',
        className,
      )}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? contentId : undefined}
        className="flex w-full items-start justify-between gap-3 bg-muted/40 px-4 py-3 text-left focus-visible:outline-offset-[-2px]"
      >
        <span className="flex min-w-0 items-start gap-3">
          {icon && (
            <span
              aria-hidden
              className={
                variant === 'soft'
                  ? 'shrink-0 flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10'
                  : 'mt-0.5 shrink-0'
              }
            >
              {icon}
            </span>
          )}
          <span className="min-w-0">
            <span className="block text-[12.5px] font-semibold text-foreground">
              {title}
              {badge && <>{' '}{badge}</>}
            </span>
            {subtitle && <span className="block text-xs text-muted-foreground">{subtitle}</span>}
            {!open && summary && (
              <span className="mt-0.5 block truncate text-xs text-muted-foreground">{summary}</span>
            )}
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            variant === 'soft'
              ? 'mt-0.5 h-4 w-4 shrink-0 transition-transform text-primary'
              : 'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div
          id={contentId}
          className={
            variant === 'soft'
              ? 'space-y-3 px-4 py-4 border-t border-border'
              : 'space-y-3 border-t border-border px-4 py-4'
          }
        >
          {children}
        </div>
      )}
    </section>
  )
}
